import ShareModal from '@medeloop/medeloop-components-library/dist/components/ShareModal/ShareModal';
import { useShareDatasets } from './useShareDatasets';
import { InvitedEntities } from '@medeloop/medeloop-components-library/dist/components/ShareModal/types';
import { DataSource, DataSourceOrganization } from 'src/types/data-sources';
import { useEffect, useState } from 'react';
import { Modal } from '@medeloop/medeloop-components-library';

interface ShareDatasetsProps {
  readonly isShareModalOpen: boolean;
  readonly setIsShareModalOpen: (isOpen: boolean) => void;
  readonly dataset: DataSourceOrganization | DataSource | null;
}

export const ShareDatasets = ({
  isShareModalOpen,
  setIsShareModalOpen,
  dataset,
}: ShareDatasetsProps) => {
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [orgAndUserNamesRemoved, setOrgAndUserNamesRemoved] = useState<string>('');
  const [finalRecipients, setFinalRecipients] = useState<InvitedEntities | null>(null);

  const {
    invitableResearchers,
    invitedResearchers,
    invitableOrganizations,
    invitedOrganizations,
    fetchingShareResearchersData,
    onShareSave,
    resetShareModalState,
    initShareModal,
  } = useShareDatasets();

  const onCancel = () => {
    resetShareModalState();
    setIsShareModalOpen(false);
  };

  const onOpenChange = (open: boolean) => {
    setIsShareModalOpen(open);
  };

  const handleOnShareSave = (recipients: InvitedEntities) => {
    const removedUsers = invitedResearchers.filter(
      (user) => !recipients.users.some((newUser) => newUser.id === user.id),
    );

    const removedOrgs = invitedOrganizations.filter(
      (org) => !recipients.organizations.some((newOrg) => newOrg.id === org.id),
    );

    if (removedUsers.length === 0 && removedOrgs.length === 0) {
      // If no users or organizations were removed, we can proceed with the save
      onShareSave(recipients);
      setIsShareModalOpen(false);
    } else {
      // Get names of removed users and organizations
      const removedUserNames = removedUsers.map((user) => user.name).join(', ');
      const removedOrgNames = removedOrgs.map((org) => org.displayName).join(', ');
      setOrgAndUserNamesRemoved(
        `${removedUserNames}${removedUserNames && removedOrgNames ? ', ' : ''}${removedOrgNames}`,
      );
      setFinalRecipients(recipients);
      setIsWarningModalOpen(true);
    }
  };

  const handleWarningModalConfirm = () => {
    onShareSave(finalRecipients as InvitedEntities);
    setIsShareModalOpen(false);
    setIsWarningModalOpen(false);
  };

  useEffect(() => {
    if (dataset && isShareModalOpen) {
      initShareModal(dataset);
    }
  }, [dataset, isShareModalOpen]);

  return (
    <>
      {isShareModalOpen && dataset && (
        <ShareModal
          title={
            dataset && 'displayName' in dataset
              ? (dataset as DataSource).displayName
              : (dataset as DataSourceOrganization).dataSourceDisplayName
          }
          isLoading={fetchingShareResearchersData}
          showCopyLink={false}
          showGeneralAccess={false}
          invitableEntities={{ users: invitableResearchers, organizations: invitableOrganizations }}
          existingInvites={{
            users: invitedResearchers,
            organizations: invitedOrganizations,
            external: [],
          }}
          maxInvitableEmails={100}
          onOpenChange={onOpenChange}
          onCancel={onCancel}
          onSave={(recipients) => handleOnShareSave(recipients as InvitedEntities)}
          translations={{
            cancel: 'Cancel',
            share: 'Share',
            save: 'Save',
            copyLink: 'Copy link',
            whoHasAccess: 'Who has access?',
            emailPlaceholder: 'Enter an email address, then press ENTER',
            emailTeamPlaceholder: 'Enter a team name or email address, then press ENTER',
            new: 'New',
            remove: 'Remove',
            canView: 'Can view',
            generalAccess: 'General access',
            everyoneIn: 'Everyone in',
            onlyInvitedPeople: 'Only invited people',
            unsavedChanges: 'Unsaved changes',
            discardChanges: 'Discard changes',
            unsafeChangesWarning:
              'You have unsaved changes. Are you sure you want to discard them and exit?',
            copiedToClipboard: 'Copied to clipboard',
            shareWithExternal: 'Share with external emails?',
            proceedWithSharing: 'Proceed with sharing',
            emailsExternalToOrganization: 'The following emails are external to your organization',
            carefulSharingInfoWarning:
              'Please be cautious when sharing to ensure the security of your information.',
          }}
        />
      )}
      {isWarningModalOpen ? (
        <Modal
          isOpen={isWarningModalOpen}
          title="Remove access to this dataset?"
          actionText="Remove access"
          actionFunction={handleWarningModalConfirm}
          actionType={'danger'}
          onCancel={() => setIsWarningModalOpen(false)}
        >
          <div className="w-96 flex flex-col gap-4">
            <div>
              This will revoke <span className="font-semibold">{orgAndUserNamesRemoved}</span>{' '}
              access to this dataset. They will no longer be able to view, analyze, or collaborate
              on it.
            </div>
            <div>This action won’t delete their account or affect access to other resources.</div>
            <div>Are you sure you want to continue?</div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};
